/**
 * TagStrip — chip strip + "+ Add Tag" combobox for a single lead.
 *
 * Renders every active tag on the lead as a colored chip with an inline
 * "×" to remove. The + Add Tag button opens a searchable list of every
 * tag in the tenant's library; typing a name that doesn't exist offers
 * a "Create <name>" row. AI-pending tags render dimmed with an Approve
 * / Reject pair. Source (system / ai / manual) is visible on hover.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Sparkles, Cog, Hand, Check } from "lucide-react";

const SOURCE_ICON = {
  system:    Cog,
  ai:        Sparkles,
  manual:    Hand,
  automation:Cog,
};

export default function TagStrip({ leadId }) {
  const [tags, setTags]         = useState([]);      // active + pending
  const [library, setLibrary]   = useState([]);
  const [showAdd, setShowAdd]   = useState(false);
  const [search, setSearch]     = useState("");
  const [busy, setBusy]         = useState(false);

  const load = async () => {
    try {
      const [{ data: leadRes }, { data: libRes }] = await Promise.all([
        api.get(`/leads/${leadId}/tags`, { params: { include_pending: true } }),
        api.get(`/tags`),
      ]);
      setTags(leadRes.tags || []);
      setLibrary(libRes.tags || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { if (leadId) load(); /* eslint-disable-next-line */ }, [leadId]);

  const addTag = async (slug) => {
    setBusy(true);
    try {
      await api.post(`/leads/${leadId}/tags`, { slug });
      await load();
      setShowAdd(false);
      setSearch("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add tag");
    } finally { setBusy(false); }
  };

  const createAndAdd = async () => {
    if (!search.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/tags`, {
        name: search.trim(), category: "custom", color: "#64748b",
      });
      await api.post(`/leads/${leadId}/tags`, { slug: data.tag.slug });
      await load();
      setShowAdd(false);
      setSearch("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create tag");
    } finally { setBusy(false); }
  };

  const removeTag = async (lt) => {
    try {
      await api.delete(`/leads/${leadId}/tags/${lt.tag_id}`);
      setTags((prev) => prev.filter((t) => t.id !== lt.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove tag");
    }
  };

  const approvePending = async (lt) => {
    try {
      await api.post(`/leads/${leadId}/tags/${lt.id}/approve`);
      await load();
    } catch (e) { toast.error("Approve failed"); }
  };

  const filtered = library.filter((t) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return t.name.toLowerCase().includes(s) || t.category.includes(s);
  });
  const exactHit = library.some((t) => t.slug === search.trim().toLowerCase().replace(/\W+/g, "_"));
  const active   = tags.filter((t) => t.status !== "pending");
  const pending  = tags.filter((t) => t.status === "pending");

  return (
    <div className="space-y-1.5" data-testid={`tag-strip-${leadId}`}>
      <div className="flex flex-wrap items-center gap-1.5">
        {active.map((t) => {
          const Icon = SOURCE_ICON[t.source] || Hand;
          return (
            <span key={t.id}
                  title={`${t.source}${t.reason ? " · " + t.reason : ""}`}
                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold text-white"
                  style={{ backgroundColor: t.tag_color || "#64748b" }}
                  data-testid={`tag-chip-${t.tag_slug}`}>
              <Icon size={9} strokeWidth={2.4} />
              {t.tag_name}
              <button onClick={() => removeTag(t)}
                      className="ml-0.5 hover:bg-black/20 rounded-full"
                      data-testid={`tag-remove-${t.tag_slug}`}>
                <X size={10} />
              </button>
            </span>
          );
        })}
        <button
          onClick={() => setShowAdd((v) => !v)}
          className="inline-flex items-center gap-1 rounded-full border border-dashed border-slate-400 px-2 py-0.5 text-[11px] text-slate-600 hover:bg-slate-100"
          data-testid="tag-add-btn">
          <Plus size={10} /> Add Tag
        </button>
      </div>

      {pending.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1 border-t border-dashed border-purple-200">
          <span className="text-[10px] uppercase tracking-widest text-purple-600 pt-0.5">
            AI suggested
          </span>
          {pending.map((t) => (
            <span key={t.id}
                  title={`confidence ${(t.confidence*100).toFixed(0)}% · ${t.reason || ""}`}
                  className="inline-flex items-center gap-1 rounded-full border border-purple-300 bg-purple-50 px-2 py-0.5 text-[11px] text-purple-800">
              <Sparkles size={9} /> {t.tag_name}
              <button onClick={() => approvePending(t)}
                      title="Approve"
                      className="ml-0.5 hover:bg-purple-200 rounded-full">
                <Check size={10} />
              </button>
              <button onClick={() => removeTag(t)}
                      title="Reject"
                      className="hover:bg-red-100 rounded-full">
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      )}

      {showAdd && (
        <div className="mt-1.5 rounded-lg border border-slate-200 bg-white p-2 shadow-sm">
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search or create a tag…"
            className="w-full rounded-md border border-slate-200 px-2 py-1 text-xs outline-none focus:border-slate-400"
            data-testid="tag-search-input"
          />
          <div className="max-h-48 overflow-y-auto mt-1.5">
            {filtered.slice(0, 20).map((t) => (
              <button
                key={t.id}
                onClick={() => addTag(t.slug)}
                disabled={busy}
                className="w-full flex items-center justify-between gap-2 px-1.5 py-1 rounded hover:bg-slate-50 text-left disabled:opacity-50"
                data-testid={`tag-option-${t.slug}`}>
                <span className="inline-flex items-center gap-1.5 text-[11px]">
                  <span className="inline-block w-2 h-2 rounded-full"
                        style={{ backgroundColor: t.color }} />
                  {t.name}
                </span>
                <span className="text-[10px] uppercase text-slate-400">
                  {t.category}{t.usage_count ? ` · ${t.usage_count}` : ""}
                </span>
              </button>
            ))}
            {!exactHit && search.trim().length >= 2 && (
              <button
                onClick={createAndAdd}
                disabled={busy}
                className="w-full flex items-center gap-1.5 px-1.5 py-1 rounded hover:bg-emerald-50 text-emerald-700 text-[11px] font-semibold disabled:opacity-50"
                data-testid="tag-create-new">
                <Plus size={11} /> Create &quot;{search.trim()}&quot; (custom)
              </button>
            )}
            {filtered.length === 0 && exactHit && (
              <div className="text-[11px] text-slate-500 px-1.5 py-1">Tag already on lead.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
